import React from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import { OwnershipType } from "../../../types/ownership";

interface OwnershipSelectorProps {
  value: OwnershipType;
  onChange: (value: OwnershipType) => void;
  entityType?: string; // "vehicle" or "trailer"
  error?: string;
}

const OwnershipSelector: React.FC<OwnershipSelectorProps> = ({
  value,
  onChange,
  entityType = "asset",
  error
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(event.target.value) as OwnershipType);
  };

  const getDisplayValue = (ownershipType: OwnershipType) => {
    // For display purposes, map specific lease types to CompanyOwned
    if (ownershipType === OwnershipType.CompanyOwnedLeasedFrom || 
        ownershipType === OwnershipType.CompanyOwnedLeasedTo) {
      return OwnershipType.CompanyOwned.toString();
    }
    return ownershipType.toString();
  };

  const showLeaseOptions = value === OwnershipType.CompanyOwnedLeasedFrom || 
                          value === OwnershipType.CompanyOwnedLeasedTo ||
                          value === OwnershipType.CompanyOwned;

  return (
    <Box>
      {/* Main Ownership Type Selection */}
      <FormControl component="fieldset" error={Boolean(error)} sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Ownership Type
          </Typography>
        </FormLabel>
        
        <RadioGroup
          value={getDisplayValue(value)}
          onChange={handleChange}
          sx={{ ml: 1 }}
        >
          <FormControlLabel
            value={OwnershipType.CompanyOwned.toString()}
            control={<Radio />}
            label={`Company Owned ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`}
          />
          <FormControlLabel
            value={OwnershipType.NotCompanyOwned.toString()}
            control={<Radio />}
            label={`Not Company Owned ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`}
          />
        </RadioGroup>
        
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </FormControl>

      {/* Lease Options - Only show when Company Owned is selected */}
      {showLeaseOptions && (
        <FormControl component="fieldset" sx={{ ml: 2, mb: 2 }}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={500}>
              Lease Options
            </Typography>
          </FormLabel>
          
          <RadioGroup
            value={value.toString()}
            onChange={handleChange}
            sx={{ ml: 1 }}
          >
            <FormControlLabel
              value={OwnershipType.CompanyOwned.toString()}
              control={<Radio size="small" />}
              label="No Lease"
            />
            <FormControlLabel
              value={OwnershipType.CompanyOwnedLeasedFrom.toString()}
              control={<Radio size="small" />}
              label="Leased From"
            />
            <FormControlLabel
              value={OwnershipType.CompanyOwnedLeasedTo.toString()}
              control={<Radio size="small" />}
              label="Leased To"
            />
          </RadioGroup>
        </FormControl>
      )}
    </Box>
  );
};

export default OwnershipSelector;
